import React from 'react';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import { useAutoScroll } from './ChatContext';
import { trans } from '../utils';

/**
 * Chat Messages - Scrollable container for message history
 */
const ChatMessages = ({ messages, isLoading, onSuggestionClick }) => {
  const {
    messagesEndRef,
    messagesContainerRef,
    showScrollButton,
    scrollToBottom,
    handleScroll
  } = useAutoScroll(messages, isLoading);

  // Suggested prompts for empty state
  const suggestions = [
    trans("What's the total value of selected assets?"),
    trans("Show me flagged items"),
    trans("Summarize the current view")
  ];

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="chat-messages" ref={messagesContainerRef}>
        <div className="chat-empty-state">
          <svg className="chat-empty-state-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          
          <h3 className="chat-empty-state-title">{trans('Ask questions about your data')}</h3>
          <p className="chat-empty-state-description">
            {trans('Get insights, analyze trends, and explore your assets through conversation.')}
          </p>
          
          <div className="chat-suggestions">
            <p style={{ fontSize: '13px', color: 'var(--chat-text-secondary)', marginBottom: '8px' }}>
              {trans('Try asking')}:
            </p>
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                className="chat-suggestion-button"
                onClick={() => onSuggestionClick?.(suggestion)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="chat-messages" 
      ref={messagesContainerRef}
      onScroll={handleScroll}
      role="log"
      aria-live="polite"
      aria-label={trans('Chat messages')}
    >
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}
      
      {isLoading && <TypingIndicator />}
      
      <div ref={messagesEndRef} />
      
      {showScrollButton && (
        <button 
          className="chat-scroll-bottom"
          onClick={() => scrollToBottom()}
          aria-label={trans('Scroll to bottom')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
          {trans('New messages')}
        </button>
      )}
    </div>
  );
};

export default ChatMessages;
